import { Outlet, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import WhatsAppFAB from "@/components/WhatsAppFAB";

const LOGO_URL = "/logo.png";

const NavLink = ({ to, children, testid }) => {
  const { pathname } = useLocation();
  const active = pathname === to || (to !== "/" && pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`text-sm tracking-wide transition-colors ${
        active
          ? "text-[color:var(--brand-primary)]"
          : "text-[color:var(--brand-text)] hover:text-[color:var(--brand-primary)]"
      }`}
    >
      {children}
    </Link>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-[color:var(--brand-bg)]/85 backdrop-blur-md border-b border-[color:var(--brand-border)]"
    >
      <div className="container-narrow flex items-center justify-between py-4">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="nav-logo">
          <img
            src={LOGO_URL}
            alt="Journey To Andaman"
            className="h-10 sm:h-12 w-auto object-contain shrink-0"
          />
          <div className="leading-tight min-w-0">
            <div className="font-heading text-xl truncate">Journey To Andaman</div>
            <div className="overline text-[10px]">Handcrafted Island Escapes</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink to="/" testid="nav-home">Home</NavLink>
          <NavLink to="/packages" testid="nav-packages">Packages</NavLink>
          <NavLink to="/contact" testid="nav-contact">Contact</NavLink>
          <a
            href="tel:+918900964069"
            data-testid="nav-call"
            className="btn-primary text-sm"
          >
            <Phone className="w-4 h-4" /> +91 89009 64069
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[color:var(--brand-border)]">
          <div className="container-narrow py-4 flex flex-col gap-4">
            <NavLink to="/" testid="nav-home-mobile">Home</NavLink>
            <NavLink to="/packages" testid="nav-packages-mobile">Packages</NavLink>
            <NavLink to="/contact" testid="nav-contact-mobile">Contact</NavLink>
            <a
              href="tel:+918900964069"
              className="btn-primary text-sm w-max"
              data-testid="nav-call-mobile"
            >
              <Phone className="w-4 h-4" /> Call us
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="bg-[color:var(--brand-surface-alt)] mt-24 border-t border-[color:var(--brand-border)]"
  >
    <div className="container-narrow py-16 grid md:grid-cols-2 lg:grid-cols-5 gap-10">
      <div className="lg:col-span-1">
        <div className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="Journey To Andaman"
            className="h-14 w-auto object-contain"
          />
          <div className="font-heading text-xl">Journey To Andaman</div>
        </div>
        <p className="text-sm text-[color:var(--brand-text-soft)] mt-3 leading-relaxed">
          Andaman specialists. Handcrafted itineraries, honest prices, no
          middlemen — since day one.
        </p>
      </div>
      <div>
        <div className="overline mb-3">Explore</div>
        <ul className="space-y-2 text-sm">
          <li><Link to="/packages" className="hover:text-[color:var(--brand-primary)]" data-testid="footer-all-packages">All Packages</Link></li>
          <li><Link to="/packages?category=Honeymoon" className="hover:text-[color:var(--brand-primary)]" data-testid="footer-cat-honeymoon">Honeymoon</Link></li>
          <li><Link to="/packages?category=Family" className="hover:text-[color:var(--brand-primary)]" data-testid="footer-cat-family">Family</Link></li>
          <li><Link to="/packages?category=Adventure" className="hover:text-[color:var(--brand-primary)]" data-testid="footer-cat-adventure">Adventure</Link></li>
          <li><Link to="/packages?category=Group" className="hover:text-[color:var(--brand-primary)]" data-testid="footer-cat-group">Group</Link></li>
          <li><Link to="/packages?category=Luxury" className="hover:text-[color:var(--brand-primary)]" data-testid="footer-cat-luxury">Luxury</Link></li>
          <li><Link to="/packages?category=Budget" className="hover:text-[color:var(--brand-primary)]" data-testid="footer-cat-budget">Budget</Link></li>
        </ul>
      </div>
      <div>
        <div className="overline mb-3">Services</div>
        <ul className="space-y-2 text-sm" data-testid="footer-services">
          <li>Tour packages</li>
          <li>Hotel booking</li>
          <li>Vehicle booking</li>
          <li>Ferry ticketing</li>
          <li>Water sports</li>
        </ul>
      </div>
      <div>
        <div className="overline mb-3">Company</div>
        <ul className="space-y-2 text-sm">
          <li><Link to="/contact" className="hover:text-[color:var(--brand-primary)]" data-testid="footer-contact-link">Contact</Link></li>
          <li><Link to="/admin/login" className="hover:text-[color:var(--brand-primary)]" data-testid="footer-admin-link">Admin</Link></li>
        </ul>
      </div>
      <div>
        <div className="overline mb-3">Reach us</div>
        <p className="text-sm">+91 89009 64069</p>
        <p className="text-sm" data-testid="footer-phone-alt">+91 96795 28328</p>
        <p className="text-sm" data-testid="footer-email">journeytoandaman@gmail.com</p>
        <p className="text-sm mt-2 text-[color:var(--brand-text-soft)]">
          Port Blair, Andaman & Nicobar Islands
        </p>
        <p className="text-xs mt-3 text-[color:var(--brand-text-soft)]" data-testid="footer-ferry-partner">
          Preferred ferry partner:{" "}
          <span className="text-[color:var(--brand-text)] font-medium">Nautika</span>
        </p>
      </div>
    </div>
    <div className="border-t border-[color:var(--brand-border)]">
      <div className="container-narrow py-5 text-xs text-[color:var(--brand-text-soft)] flex flex-col md:flex-row md:justify-between gap-2">
        <span>© {new Date().getFullYear()} Journey To Andaman. All rights reserved.</span>
        <span>Made with care, from the islands.</span>
      </div>
    </div>
  </footer>
);

export default function SiteLayout() {
  return (
    <div data-testid="site-layout" className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
}
